import { Engine } from './core/engine.js';
import { Renderer } from './render/renderer.js';
import { createDatabase, seedNewGame, Repo } from './data/schema.js';
import { createHomeScene, HOME_CFG } from './scenes/home.js';
import { installActions } from './systems/actions.js';
import { HUD } from './ui/hud.js';
import { TouchControls, isTouchDevice } from './ui/touch.js';
import { NetProxy } from './net/net.js';
import { Account } from './net/account.js';
import { installCoop } from './systems/coop.js';
import { Walkie } from './net/voice.js';
import { TVPanel } from './ui/tv.js';
import { BookshelfPanel } from './ui/bookshelf.js';
import { WhiteboardPanel } from './ui/whiteboard.js';
import { NetPanel } from './ui/netpanel.js';
import { installLayout } from './ui/layout.js';
import { ScreenCtl } from './ui/screen.js';
import { installCameraZoom } from './ui/zoom.js';
import { EditMode } from './ui/editmode.js';
import { MapBuilder } from './ui/mapbuilder.js';
import { ItemForge } from './ui/itemforge.js';
import { loadDefsFromStorage } from './data/items.js';
import { installExpansion } from './data/expansion.js';
import { registerMapScenes } from './data/world.js';
import { CityPanels } from './ui/city.js';
import { GaragePanel } from './ui/garage.js';
import { Dashboard } from './ui/dashboard.js';
import { MinimapUI } from './ui/minimap.js';
import { InsightPanel } from './ui/insight.js';
import { Tags } from './systems/tags.js';
import { Inspire } from './systems/inspire.js';
import { Jobs } from './systems/jobs.js';
import { Neighbor } from './systems/neighbor.js';
import { EV } from './core/eventbus.js';

/**
 * 引导入口：组装引擎 -> 挂载场景 -> 装载玩法 -> 启动。
 * 这里只做「接线」，不写具体玩法。
 */
function boot() {
  // 楷书字体：开了门再挂上（首屏那十几个分片会把引擎挤到最后才下载），
  // 没加载完这段时间由系统楷体顶着，加载完界面与画面自己变成霞鹜文楷
  const fontLink = document.createElement('link');
  fontLink.rel = 'stylesheet';
  fontLink.href = './fonts/lxgw/style.css';
  document.head.appendChild(fontLink);

  const canvas = document.getElementById('game');

  // 版面：先算好画面与界面的缩放，再挂游戏
  installLayout();

  // 「从城郊到城市」这一章的东西（垃圾、废车、铺面、衙署口）先挂进表，
  // 后面的场景、器物铸造才知道有这些条目
  installExpansion();

  const db = createDatabase();
  const renderer = new Renderer(canvas, { tileSize: HOME_CFG.TS });
  const engine = new Engine({ canvas, db, renderer });

  const home = createHomeScene(engine);
  engine.registerScene(home);

  installActions(engine);

  const hud = new HUD(engine);
  engine.hud = hud;

  // 布置（编辑）模式：拖家具、放新家具、收起
  const edit = new EditMode(engine, { toast: (text, kind) => hud.toast(text, kind) });
  engine.edit = edit;
  const btnEdit = document.getElementById('btn-edit');
  if (btnEdit) btnEdit.onclick = () => edit.toggle();

  // 镜头缩放：手机双指捏合、电脑滚轮、+ / - / 0
  installCameraZoom(engine, canvas);

  // 全屏 / 横屏：顶栏按钮 + F 键；进门那一刻会顺手试一次（见 startBtn）
  const screenCtl = new ScreenCtl({ toast: (text, kind) => hud.toast(text, kind) });

  // 触屏控制：粗指针设备自动启用；桌面可用 ?touch=1 或顶栏「摇杆」按钮打开
  const touch = new TouchControls(engine.input, {
    // 手机顶栏放不下存档按钮，改由右下角触屏键触发
    onAction: (act) => { if (act === 'save') hud.saveNow(); },
  });
  engine.touch = touch;

  const btnTouch = document.getElementById('btn-touch');
  const markTouchBtn = (on) => {
    if (!btnTouch) return;
    btnTouch.style.borderColor = on ? 'var(--accent)' : '';
    btnTouch.style.color = on ? 'var(--accent)' : '';
  };
  if (btnTouch) {
    btnTouch.onclick = () => {
      markTouchBtn(touch.toggle());
      // 开场遮罩盖在摇杆之上，先给个说明免得以为没生效
      if (touch.enabled && !document.getElementById('splash').classList.contains('hidden')) {
        hud.toast('摇杆已开启，进门后可见', 'good');
      }
    };
  }
  const forceTouch = new URLSearchParams(location.search).get('touch') === '1';
  if (isTouchDevice() || forceTouch) {
    touch.enable();
    markTouchBtn(true);
  }

  // 联机：网管 -> 访客系统 -> 串门 / 电视面板（换传输实现不用改这些）
  const account = new Account();
  const net = new NetProxy();
  net.identity = account.identity;   // 同一对象：登录只改字段，重连不会换人
  engine.account = account;
  const coop = installCoop(engine, net);
  // 电视与游戏桌是两块各管各的面板：各记各的网址、各开各的小窗，换台互不影响
  const tv = new TVPanel(engine, net, { kind: 'tv' });
  const table = new TVPanel(engine, net, { kind: 'gametable' });
  engine.table = table;
  const shelf = new BookshelfPanel(engine);
  // 白板（灵感 / 日记）：立在电视右手边，按 E 就写；网址那栏能把电视或牌桌换台。
  // 屋里的人共用这一块：谁写的都会同步过去，传 net 就是为了走这条线。
  const board = new WhiteboardPanel(engine, { tv, table, net });
  engine.board = board;

  // 对讲机：按住 V（或触屏「讲」键）说话，松手发给屋里的人。
  // 语音只在内存里过一趟，不写库、不进书架、不占服务器那点额度。
  const walkie = new Walkie(engine, net, { toast: (text, kind) => hud.toast(text, kind) });
  engine.walkie = walkie;
  // 有人说话：报一声是谁，头顶也会挂气泡
  engine.events.on(EV.VOICE_IN, (v) => {
    hud.toast(`${String(v.name || '有人').slice(0, 6)}说了一段话`, 'info');
  });
  // 录音 / 发送中的小牌子：挂在画面上方，说完就撤
  const voiceTag = document.getElementById('voice-rec');
  const voiceText = voiceTag && voiceTag.querySelector('span');
  engine.events.on(EV.VOICE_STATE, (v) => {
    if (!voiceTag) return;
    const on = v.state === 'rec' || v.state === 'send';
    voiceTag.classList.toggle('hidden', !on);
    if (voiceText) voiceText.textContent = v.state === 'rec' ? '松开就发出去' : '正在发出去';
  });

  const netPanel = new NetPanel(engine, net, { coop, account, walkie });
  // 城里的几块面板（买卖 / 告示榜 / 衙署 / 地皮 / 短工市）：走到柜台、榜前按 E 开
  const city = new CityPanels(engine, { toast: (text, kind) => hud.toast(text, kind) });
  engine.city = city;
  // 攒车面板（走到工作台 / 车铺工位按 E）+ 车上的仪表
  const garage = new GaragePanel(engine, { toast: (text, kind) => hud.toast(text, kind) });
  const dash = new Dashboard(engine);
  // 舆图：右上角一张草图，Tab 摊开
  const minimap = new MinimapUI(engine);
  engine.garage = garage;
  engine.dash = dash;
  engine.minimap = minimap;
  // 消息里带是哪件家具，各自认领；布置改动由 edit 认领（同屋共摆一套家具）
  net.on((msg) => { tv.onNet(msg); table.onNet(msg); edit.onNet(msg); });
  edit.net = net;
  engine.net = net;
  engine.coop = coop;

  // 营造司：改过的数据活在 localStorage，开局先读回来，再把自制地图注册成正式场景
  loadDefsFromStorage();
  registerMapScenes(engine);

  // 地图编辑器（画地图用，不是玩法）+ 什物谱（物品 / 器物编辑器）
  const builder = new MapBuilder(engine, { toast: (text, kind) => hud.toast(text, kind) });
  const forge = new ItemForge(engine, { toast: (text, kind) => hud.toast(text, kind) });
  engine.builder = builder;
  engine.forge = forge;
  const btnForge = document.getElementById('btn-forge');
  if (btnForge) btnForge.onclick = () => forge.toggle();
  // 什物谱里的「画地图」：两个工具互相让位，同一时刻只开一个
  const forgeMapBtn = document.getElementById('forge-map');
  if (forgeMapBtn) forgeMapBtn.onclick = () => { forge.close(); builder.toggle(true); };

  // Tab：舆图摊开 / 收起。浏览器默认要把焦点挪走，这里必须拦下来
  window.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab') return;
    const a = document.activeElement;
    // 正在写字（书架上传、电视找片子）就不抢这一下
    if (a && (a.tagName === 'INPUT' || a.tagName === 'TEXTAREA' || a.isContentEditable)) return;
    e.preventDefault();
    minimap.toggleBig();
  });

  window.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    // 舆图摊着的时候，Esc 先收回舆图
    if (minimap.big) { minimap.toggleBig(false); return; }
    // 布置中：先取消"正在放置"，再按一次才退出布置
    if (edit.active) {
      if (edit.placing) { edit.placing = null; edit.ghost = null; edit._buildList(); edit._syncPanel(); }
      else edit.toggle(false);
    }
    // 电视 / 牌桌全屏时，Esc 先退出全屏；这一下别把面板也关了
    if (tv.isFullscreen()) { tv.exitFullscreen(); return; }
    if (table.isFullscreen()) { table.exitFullscreen(); return; }
    tv.close();
    table.close();
    shelf.close();
    board.close();
    forge.close();
    city.close();
    garage.close();
  });

  // 「忽然明白了什么」那一下的亮相：不占屏、不挡手，说完自己淡掉
  const insight = new InsightPanel(engine);

  // 天亮就结算：学到的本事慢慢生疏；委托到期、证照推进、雇工的工钱都在这时候走一遍
  engine.events.on(EV.TIME_NEWDAY, () => {
    const w0 = Repo.getWorld(db);
    // 天亮这件事件会被发上两回（时钟推进一回，睡醒一回），
    // 结算只走一遍：不然工钱扣两次、回礼发两份、本事也消退两回。
    if (Repo.getFlag(db, `settled:${w0.day}`)) return;
    Repo.setFlag(db, `settled:${w0.day}`, true);

    Tags.decayDaily(db);
    for (const msg of Jobs.daily(db)) hud.toast(msg, 'info');
    // 天亮时看一眼：手里的念头有没有撞出个所以然（一天只推一次）
    Inspire.tickDaily(db, engine.events);
    // 昨日舍出去的，今日门口搁着什么
    for (const msg of Neighbor.daily(db)) hud.toast(msg, 'good');
  });

  // 只要有任何面板（封面 / 行囊 / 说明 / 电视 / 串门）开着，就收起摇杆与动作按钮：
  // 一来按钮不会压在面板上，二来摇杆热区不再和面板里的点按、拖拽抢手指
  // 注意：#rotate 竖屏提示是 CSS 媒体查询控制的显示，class 里没有 hidden，不能放进这个列表
  const guardPanels = ['splash', 'bag', 'help', 'tv', 'gametable', 'whiteboard', 'net', 'shelf', 'edit', 'forge', 'build',
    'shop', 'board', 'license', 'stall', 'hire', 'assemble']
    .map((id) => document.getElementById(id))
    .filter(Boolean);
  const syncTouchGuard = () => {
    // 电视小窗不算占屏：挂着它还能照常走路，所以只有整块的面板才收起触屏控件
    const open = guardPanels.some((el) => !el.classList.contains('hidden')
      && !el.classList.contains('tv-mini'));
    document.body.classList.toggle('modal-open', open);
    // 面板一开就松手：摇杆与键盘一起清。键盘也得清——面板里点进 iframe（书架的网页、
    // 电视的画面）之后焦点就不在父窗口了，keyup 收不着，Shift 会卡在按下的状态，
    // 关掉面板人还在飞跑
    if (open) {
      if (touch) touch.releaseAll();
      if (engine.input) engine.input.clear();
    }
    else if (!open) {
      // 关面板时把焦点从输入框上摘下来：手机上软键盘会跟着收起，
      // 免得键盘占着半屏、摇杆被挤得看不见，回来还以为角色走不动了
      const a = document.activeElement;
      if (a && a !== document.body && typeof a.blur === 'function' && a.closest && a.closest('.panel')) a.blur();
    }
  };
  if (typeof MutationObserver !== 'undefined') {
    // 注意：observer 必须挂个长期引用（挂到 touch 实例上），否则 boot 返回后会被 GC，回调从此失联
    touch._panelObserver = new MutationObserver(syncTouchGuard);
    for (const el of guardPanels) touch._panelObserver.observe(el, { attributes: true, attributeFilter: ['class'] });
  }
  syncTouchGuard();

  // 全局按键（与场景无关，最后执行，负责 UI 与存档）
  engine.globalKeys = {
    name: 'globalKeys',
    update(dt, ctx) {
      const { input, engine: eng, db: database } = ctx;
      if (input.justPressed('g') || input.justPressed('F3')) renderer.showGrid = !renderer.showGrid;
      if (input.justPressed('b')) hud.toggle('bag');
      if (input.justPressed('h')) hud.toggle('help');
      if (input.justPressed('m')) edit.toggle();
      // F8 什物谱（物品 / 器物），Shift+F8 地图编辑器
      if (input.justPressed('F8')) {
        if (input.isDown('Shift')) { forge.close(); builder.toggle(); }
        else { builder.toggle(false); forge.toggle(); }
      }
      if (input.justPressed('p')) eng.togglePause();
      // 对讲机：按住 V 说话，松手发出去。正在输入框里写字时不抢这个键
      const focused = document.activeElement;
      const typing = !!focused
        && (focused.tagName === 'INPUT' || focused.tagName === 'TEXTAREA' || focused.isContentEditable);
      if (!typing) {
        if (input.justPressed('v')) walkie.start();
        if (input.justReleased('v')) walkie.stop();
      }
      if (input.justPressed('F9')) {
        if (database.load()) {
          home.loaded = false;
          eng.enterScene('home');
          hud.toast('已读档', 'good');
        } else {
          hud.toast('没有可读取的存档', 'warn');
        }
      }
      hud.update(dt);
      dash.update(dt);
      garage.update(dt);     // 攒车读条：跟着引擎的钟走，暂停就停
      minimap.update(dt);    // 舆图：隔一会儿重画一次就够
    },
  };

  const splash = document.getElementById('splash');
  // 引擎起来了，撤掉封面那句「正在拾掇屋子」，这时按钮才真的能开门
  const splashTip = document.getElementById('splash-tip');
  if (splashTip) splashTip.remove();
  const startBtn = document.getElementById('btn-start');
  const hasSave = db.hasSave();
  if (hasSave) startBtn.textContent = '继续上次';

  // 竖屏提示里的「我就竖着玩」：收起提示，照玩（画面会小一些）
  const stayBtn = document.getElementById('btn-stay');
  if (stayBtn) stayBtn.onclick = () => document.getElementById('rotate').classList.add('hidden');

  // 竖屏提示里的「全屏并横过来」：先全屏（手势内），再请系统转向；成了就收起提示
  const rotateFullBtn = document.getElementById('btn-rotate-full');
  if (rotateFullBtn) {
    rotateFullBtn.onclick = async () => {
      const ok = await screenCtl.enter();
      const landscape = window.innerWidth > window.innerHeight;
      if (ok && landscape) document.getElementById('rotate').classList.add('hidden');
      else hud.toast('横不过来就请手动转一下手机', 'warn');
    };
  }

  // 手机上进门即全屏（最自然）；桌面上只在你曾经手动全屏过、或地址带 ?fullscreen=1 时才自动
  const wantAutoFull = isTouchDevice()
    || ScreenCtl.wanted()
    || new URLSearchParams(location.search).get('fullscreen') === '1';

  startBtn.onclick = () => {
    // 全屏必须由用户手势发起，这一下点击正好是：同步调用，别放到 await 之后
    if (wantAutoFull) screenCtl.enter().catch(() => {});

    splash.classList.add('hidden');
    // 封面期间不显示任何界面，推门之后才亮出来
    document.body.classList.remove('booting');

    if (hasSave) {
      db.load();
    } else {
      seedNewGame(db, {
        player: { x: home.tilemap.spawn.x, y: home.tilemap.spawn.y, facing: 'down' },
      });
    }

    engine.enterScene('home');
    engine.scene.use(engine.globalKeys);
    engine.start();

    hud.refreshClock();
    hud.refreshStats();
    hud.toast(hasSave ? '回来了。' : '你推开了洞天居的门。', 'good');
    engine.events.emit(EV.GAME_READY, { engine });

    // 地址里带了房间号就自动串门：?room=1234&mode=remote
    if (new URLSearchParams(location.search).has('room')) netPanel.join();
  };

  // 调试出口
  window.__dongtian = { engine, db, renderer, hud, touch, net, coop, account, tv, board, netPanel, shelf, edit, walkie, screen: screenCtl, insight };
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
